import React from 'react';

import { cssMap, cx } from '@atlaskit/css';
import { Box, Inline, Stack } from '@atlaskit/primitives/compiled';
import Skeleton from '@atlaskit/skeleton';
import { token } from '@atlaskit/tokens';

import { FeedbackBanner } from './FeedbackBanner';
import { SmartLinkCardSkeleton } from './session-parent-entity/SmartLinkCardSkeleton';

const styles = cssMap({
	root: {
		height: '100%',
		width: '100%',
		boxSizing: 'border-box',
		overflowY: 'auto',
	},
	wrapper: {
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.250'),
		marginTop: token('space.250'),
		marginBottom: token('space.250'),
		marginLeft: token('space.250'),
		marginRight: token('space.250'),
	},
	card: {
		display: 'flex',
		flexDirection: 'column',
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		borderRadius: token('radius.xlarge'),
		backgroundColor: token('elevation.surface'),
		overflow: 'clip',
	},
	row: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: token('space.150'),
		paddingBlock: token('space.150'),
		paddingInline: token('space.200'),
	},
	borderBottom: {
		borderBottomWidth: token('border.width'),
		borderBottomStyle: 'solid',
		borderBottomColor: token('color.border'),
	},
	cardBody: {
		paddingBlock: token('space.150'),
		paddingInline: token('space.200'),
	},
	timelineRow: {
		display: 'grid',
		gridTemplateColumns: '24px 1fr',
		columnGap: token('space.150'),
		alignItems: 'stretch',
	},
	rail: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		width: '24px',
	},
	connector: {
		width: '2px',
		flexGrow: 1,
		backgroundColor: token('color.background.neutral'),
	},
	timelineText: {
		paddingBottom: token('space.200'),
		minWidth: '0px',
	},
	section: {
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.150'),
	},
	artefactRow: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.150'),
		minHeight: '64px',
		paddingInline: token('space.150'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		borderRadius: token('radius.xlarge'),
	},
});

/** Agent context and Rovo Space rows of the Context card. */
const CONTEXT_ROWS = [
	{ labelWidth: 112, descriptionWidth: 88, trailingWidth: 16 },
	{ labelWidth: 96, descriptionWidth: 104, trailingWidth: 16 },
];

/** Entries of the expanded progress timeline. */
const PROGRESS_ROWS = [
	{ titleWidth: 120, descriptionWidth: 168 },
	{ titleWidth: 96 },
	{ titleWidth: 140, descriptionWidth: 72 },
	{ titleWidth: 104 },
];

const ARTEFACT_ROWS = [{ titleWidth: 150 }, { titleWidth: 190 }];

const HeadingSkeleton = ({ width }: { width: number }): React.JSX.Element => (
	<Skeleton width={width} height={16} isShimmering />
);

/**
 * Loading placeholder for {@link SessionRightPanel}. Mirrors the panel's sections
 * -feedback banner, actions, progress timeline, output, context card and parent work item
 */
export const SessionRightPanelSkeleton = (): JSX.Element => (
	<Box xcss={styles.root}>
		<Box xcss={styles.wrapper}>
			<FeedbackBanner />

			<Box xcss={styles.section}>
				<HeadingSkeleton width={56} />
				<Skeleton width="100%" height={32} borderRadius={token('radius.small')} isShimmering />
			</Box>

			<Box xcss={styles.card}>
				<Box xcss={cx(styles.row, styles.borderBottom)}>
					<HeadingSkeleton width={64} />
					<Inline space="space.100" alignBlock="center">
						<Skeleton width={44} height={20} borderRadius={token('radius.full')} isShimmering />
						<Skeleton width={16} height={16} borderRadius={token('radius.small')} isShimmering />
					</Inline>
				</Box>
				<Box xcss={styles.cardBody}>
					{PROGRESS_ROWS.map(({ titleWidth, descriptionWidth }, index) => (
						<Box key={titleWidth} xcss={styles.timelineRow}>
							<Box xcss={styles.rail}>
								<Skeleton width={16} height={16} borderRadius={token('radius.full')} isShimmering />
								{index < PROGRESS_ROWS.length - 1 && <Box xcss={styles.connector} />}
							</Box>
							<Box xcss={styles.timelineText}>
								<Stack space="space.025">
									<Skeleton width={titleWidth} height={14} isShimmering />
									{descriptionWidth !== undefined && (
										<Skeleton width={descriptionWidth} height={12} isShimmering />
									)}
								</Stack>
							</Box>
						</Box>
					))}
				</Box>
			</Box>

			<Box xcss={styles.card}>
				<Box xcss={cx(styles.row, styles.borderBottom)}>
					<HeadingSkeleton width={52} />
					<Skeleton width={16} height={16} borderRadius={token('radius.small')} isShimmering />
				</Box>
				<Box xcss={styles.cardBody}>
					<Stack space="space.050">
						{ARTEFACT_ROWS.map(({ titleWidth }) => (
							<Box key={titleWidth} xcss={styles.artefactRow}>
								<Skeleton
									width={32}
									height={32}
									borderRadius={token('radius.small')}
									isShimmering
								/>
								<Stack space="space.025">
									<Skeleton width={titleWidth} height={14} isShimmering />
									<Skeleton width={100} height={12} isShimmering />
								</Stack>
							</Box>
						))}
					</Stack>
				</Box>
			</Box>

			<Box xcss={styles.card}>
				<Box xcss={cx(styles.row, styles.borderBottom)}>
					<HeadingSkeleton width={60} />
				</Box>
				{CONTEXT_ROWS.map(({ labelWidth, descriptionWidth, trailingWidth }, index) => (
					<Box
						key={labelWidth}
						xcss={cx(styles.row, index < CONTEXT_ROWS.length - 1 && styles.borderBottom)}
					>
						<Stack space="space.025">
							<Skeleton width={labelWidth} height={16} isShimmering />
							{descriptionWidth !== undefined && (
								<Skeleton width={descriptionWidth} height={12} isShimmering />
							)}
						</Stack>
						<Skeleton
							width={trailingWidth}
							height={16}
							borderRadius={token('radius.small')}
							isShimmering
						/>
					</Box>
				))}
			</Box>

			<SmartLinkCardSkeleton />
		</Box>
	</Box>
);
